type VoxelIJKChange = {
  i: number;
  j: number;
  k: number;
  value: number;
  weight?: number;
};

type LabelDataLike = {
  getDimensions?: () => [number, number, number];
  getPointData?: () => {
    getScalars?: () => {
      getData?: () => Uint8Array | Int16Array;
      modified?: () => void;
    };
  };
  modified?: () => void;
};

const getLabelDataParts = (labelData: LabelDataLike | undefined | null) => {
  const dims = labelData?.getDimensions?.();
  const scalars = labelData
    ?.getPointData?.()
    ?.getScalars?.()
    ?.getData?.();
  return { dims, scalars };
};

const markLabelDataModified = (labelData: LabelDataLike) => {
  labelData.getPointData?.().getScalars?.().modified?.();
  labelData.modified?.();
};

export const applyVoxelChangesToLabelData = ({
  labelData,
  changes,
  originalValues,
  onlyIfCurrentValue,
}: {
  labelData: LabelDataLike | undefined | null;
  changes: VoxelIJKChange[];
  originalValues: Map<string, number>;
  onlyIfCurrentValue?: number;
}) => {
  const { dims, scalars } = getLabelDataParts(labelData);
  if (!dims || !scalars) return 0;

  const nx = dims[0];
  const ny = dims[1];
  const nz = dims[2];
  let applied = 0;

  for (const v of changes) {
    if (
      v.i < 0 ||
      v.j < 0 ||
      v.k < 0 ||
      v.i >= nx ||
      v.j >= ny ||
      v.k >= nz
    ) {
      continue;
    }
    const flat = v.k * nx * ny + v.j * nx + v.i;
    if (flat < 0 || flat >= scalars.length) continue;
    if (
      onlyIfCurrentValue !== undefined &&
      scalars[flat] !== onlyIfCurrentValue
    ) {
      continue;
    }
    const nextValue = v.value === 255 ? 255 : 0;
    const key = `${v.i}:${v.j}:${v.k}`;
    if (!originalValues.has(key)) {
      originalValues.set(key, Number(scalars[flat]));
    }
    if (scalars[flat] === nextValue) continue;
    scalars[flat] = nextValue;
    applied += 1;
  }

  if (applied > 0 && labelData) {
    markLabelDataModified(labelData);
  }
  return applied;
};

export const restoreLumenLabelDataApplyPreview = ({
  labelData,
  originalValues,
}: {
  labelData: LabelDataLike | undefined | null;
  originalValues: Map<string, number>;
}) => {
  const { dims, scalars } = getLabelDataParts(labelData);
  if (!dims || !scalars || originalValues.size === 0) {
    return 0;
  }

  const nx = dims[0];
  const ny = dims[1];
  let restored = 0;

  originalValues.forEach((value, key) => {
    const [i, j, k] = key.split(':').map(Number);
    if (
      !Number.isFinite(i) ||
      !Number.isFinite(j) ||
      !Number.isFinite(k) ||
      i < 0 ||
      j < 0 ||
      k < 0 ||
      i >= dims[0] ||
      j >= dims[1] ||
      k >= dims[2]
    ) {
      return;
    }
    const flat = k * nx * ny + j * nx + i;
    if (flat < 0 || flat >= scalars.length) return;
    if (scalars[flat] === value) return;
    scalars[flat] = value;
    restored += 1;
  });

  originalValues.clear();
  if (restored > 0 && labelData) {
    markLabelDataModified(labelData);
  }
  return restored;
};

export const restoreLumenLabelDataApplyPreviewForChanges = ({
  labelData,
  originalValues,
  changes,
}: {
  labelData: LabelDataLike | undefined | null;
  originalValues: Map<string, number>;
  changes: VoxelIJKChange[];
}) => {
  const { dims, scalars } = getLabelDataParts(labelData);
  if (!dims || !scalars || originalValues.size === 0) {
    return 0;
  }

  const nx = dims[0];
  const ny = dims[1];
  let restored = 0;
  const keys = new Set<string>();

  changes.forEach((v) => {
    if (
      !Number.isFinite(v.i) ||
      !Number.isFinite(v.j) ||
      !Number.isFinite(v.k) ||
      v.i < 0 ||
      v.j < 0 ||
      v.k < 0 ||
      v.i >= dims[0] ||
      v.j >= dims[1] ||
      v.k >= dims[2]
    ) {
      return;
    }
    keys.add(`${v.i}:${v.j}:${v.k}`);
  });

  keys.forEach((key) => {
    const originalValue = originalValues.get(key);
    if (originalValue === undefined) return;
    const [i, j, k] = key.split(':').map(Number);
    const flat = k * nx * ny + j * nx + i;
    if (flat < 0 || flat >= scalars.length) return;
    if (scalars[flat] !== originalValue) {
      scalars[flat] = originalValue;
      restored += 1;
    }
    originalValues.delete(key);
  });

  if (restored > 0 && labelData) {
    markLabelDataModified(labelData);
  }
  return restored;
};
